import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

// Shared editorial template so every program has the same feel.
// Individual pages hand in copy, image, tagline, pillars, and impact numbers.
export default function ProgramPage({
    eyebrow,
    title,
    subtitle,
    tagline,
    hero_image,
    intro,
    body_paragraphs = [],
    pillars = [],
    verse,
    stats = [],
    cta_text = "Get Involved",
}) {
    return (
        <div data-testid={`program-page-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            {/* Hero */}
            <section className="px-6 md:px-12 lg:px-20 pt-16 md:pt-24 pb-16 md:pb-24">
                <Link to="/programs" className="inline-flex items-center gap-2 eyebrow text-black/50 hover:text-[#B8860B] mb-10 text-[11px]">
                    ← All Programs
                </Link>
                <div className="grid md:grid-cols-12 gap-10 items-end">
                    <div className="md:col-span-7">
                        <div className="eyebrow text-[#B8860B] mb-6">{eyebrow || "A Program of The Gift of Kindness"}</div>
                        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95] mb-8">
                            {title}
                        </h1>
                        {subtitle && (
                            <p className="text-xl md:text-2xl font-serif italic text-black/60 mb-8 leading-snug">
                                {subtitle}
                            </p>
                        )}
                        {tagline && (
                            <p className="eyebrow text-[#B8860B] tracking-[0.28em]">{tagline}</p>
                        )}
                    </div>
                    <div className="md:col-span-5">
                        {hero_image && (
                            <img src={hero_image} alt={title} className="w-full h-[440px] md:h-[520px] object-cover" />
                        )}
                    </div>
                </div>
            </section>

            {/* Intro / Mission */}
            {intro && (
                <section className="bg-[#FAFAFA] px-6 md:px-12 lg:px-20 py-20 md:py-28 border-y border-black/10">
                    <div className="grid md:grid-cols-12 gap-10 max-w-6xl">
                        <div className="md:col-span-3">
                            <div className="eyebrow text-[#B8860B] md:sticky md:top-32">Our Mission</div>
                        </div>
                        <div className="md:col-span-8 md:col-start-5">
                            <p className="font-serif text-2xl md:text-3xl leading-snug text-black/85">{intro}</p>
                        </div>
                    </div>
                </section>
            )}

            {/* Body paragraphs */}
            {body_paragraphs.length > 0 && (
                <section className="px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="grid md:grid-cols-12 gap-10 max-w-6xl">
                        <div className="md:col-span-3">
                            <div className="eyebrow text-[#B8860B] md:sticky md:top-32">What We Do</div>
                        </div>
                        <div className="md:col-span-8 md:col-start-5 space-y-6 text-lg text-black/70 font-light leading-relaxed">
                            {body_paragraphs.map((p, i) => (
                                <p key={i}>{p}</p>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Pillars */}
            {pillars.length > 0 && (
                <section className="bg-[#FAFAFA] px-6 md:px-12 lg:px-20 py-20 md:py-28 border-y border-black/10">
                    <div className="grid md:grid-cols-3 gap-10 md:gap-14">
                        {pillars.map((b, i) => (
                            <div key={b.t}>
                                <div className="font-serif text-6xl text-[#D4AF37] mb-4">
                                    {String(i + 1).padStart(2, "0")}
                                </div>
                                <h3 className="font-serif text-2xl tracking-tight mb-4">{b.t}</h3>
                                <p className="text-black/60 font-light leading-relaxed">{b.b}</p>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Stats */}
            {stats.length > 0 && (
                <section className="px-6 md:px-12 lg:px-20 py-20 md:py-28">
                    <div className="grid md:grid-cols-3 gap-10 max-w-5xl">
                        {stats.map((s, i) => (
                            <div key={i} className="border-t border-[#D4AF37] pt-6">
                                <div className="font-serif text-5xl md:text-6xl tracking-tight tabular-nums">
                                    {s.value}
                                </div>
                                <div className="eyebrow text-black/60 mt-3 text-[11px]">{s.label}</div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Verse / Quote */}
            {verse && (
                <section className="px-6 md:px-12 lg:px-20 pb-20 md:pb-28">
                    <div className="max-w-4xl">
                        <blockquote className="border-l-2 border-[#D4AF37] pl-8 font-serif italic text-2xl md:text-3xl leading-snug text-black/80">
                            "{verse.text}"
                        </blockquote>
                        {verse.attribution && (
                            <div className="eyebrow text-[#B8860B] mt-6 pl-8">— {verse.attribution}</div>
                        )}
                    </div>
                </section>
            )}

            {/* CTA */}
            <section className="bg-[#0A0A0A] text-white px-6 md:px-12 lg:px-20 py-20 md:py-28">
                <div className="grid md:grid-cols-12 items-center gap-10 max-w-6xl">
                    <div className="md:col-span-8">
                        <div className="eyebrow text-[#D4AF37] mb-6">Join The Movement</div>
                        <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
                            Want to help, partner, or share your story?
                        </h2>
                        <p className="text-white/70 font-light text-lg mt-6 max-w-2xl">
                            Every program we run is built on the shoulders of listeners like you. Reach out and let's make room for one more act of kindness.
                        </p>
                    </div>
                    <div className="md:col-span-4 md:text-right">
                        <Link
                            to="/apply"
                            data-testid="program-cta"
                            className="inline-flex items-center gap-3 bg-[#D4AF37] text-black px-8 py-4 hover:bg-white transition-colors eyebrow"
                        >
                            {cta_text} <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
